import { useMemo } from 'react';
import { MetricInfoButton } from '@/components/MetricInfoButton';
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Brush,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';
import { useLoadCurves } from '@/hooks/useClosedLoopAnalytics';
import type { LoadCurvePoint } from '@/types/closedLoopAnalytics';

interface PMCTooltipEntry {
  name?: string;
  value?: number;
  color?: string;
}

function PMCTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: PMCTooltipEntry[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="px-3 py-2.5 rounded-lg border border-white/[0.12] bg-[rgba(12,16,24,0.92)] backdrop-blur-[20px] shadow-lg">
      <p className="text-[10px] font-mono tracking-[0.1em] text-muted-foreground mb-1.5 uppercase">
        {label}
      </p>
      {payload.map((entry: PMCTooltipEntry, i: number) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-[10px] font-mono text-muted-foreground">{entry.name}:</span>
          <span className="text-xs font-mono tabular-nums text-foreground">
            {typeof entry.value === 'number' ? entry.value.toFixed(1) : '—'}
          </span>
        </div>
      ))}
    </div>
  );
}

interface ClosedLoopPMCChartProps {
  className?: string;
}

export function ClosedLoopPMCChart({ className }: ClosedLoopPMCChartProps) {
  const { data, isLoading } = useLoadCurves();

  const chartData = useMemo(() => {
    if (!data?.points?.length) return [];
    return data.points.map((p: LoadCurvePoint) => {
      let dateLabel = p.date;
      try {
        dateLabel = format(parseISO(p.date), 'MMM d');
      } catch {
        // keep dateLabel as p.date on parse error
      }
      return { ...p, dateLabel };
    });
  }, [data]);

  if (isLoading) {
    return (
      <div className={cn('p-6 rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[20px]', className)}>
        <p className="text-xs font-mono text-muted-foreground text-center animate-pulse">LOADING PMC DATA...</p>
      </div>
    );
  }

  if (chartData.length === 0) {
    return null;
  }

  return (
    <div className={cn(
      'p-4 rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[20px]',
      'shadow-[inset_0_1px_0_rgba(0,255,200,0.04)]',
      className,
    )}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-1 h-1 rounded-full bg-[hsl(170,80%,45%)]" />
          <span className="text-[9px] font-mono tracking-[0.2em] uppercase text-muted-foreground">
            CLOSED-LOOP PMC
          </span>
          <MetricInfoButton metricKey="pmc" />
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-[2px] rounded bg-[hsl(170,80%,45%)]" />
            <span className="text-[9px] font-mono text-muted-foreground">CTL</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-[2px] rounded bg-[hsl(35,85%,55%)]" />
            <span className="text-[9px] font-mono text-muted-foreground">ATL</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-[2px] rounded bg-[hsl(200,65%,55%)]" />
            <span className="text-[9px] font-mono text-muted-foreground">TSB</span>
          </div>
        </div>
      </div>

      {/* Current values */}
      <div className="flex items-center gap-4 mb-3">
        <div>
          <span className="text-[9px] font-mono text-muted-foreground uppercase tracking-wider">CTL</span>
          <span className="ml-1.5 text-sm font-mono tabular-nums text-[hsl(170,80%,45%)]">
            {data?.current_ctl?.toFixed(1) ?? '—'}
          </span>
        </div>
        <div>
          <span className="text-[9px] font-mono text-muted-foreground uppercase tracking-wider">ATL</span>
          <span className="ml-1.5 text-sm font-mono tabular-nums text-[hsl(35,85%,55%)]">
            {data?.current_atl?.toFixed(1) ?? '—'}
          </span>
        </div>
        <div>
          <span className="text-[9px] font-mono text-muted-foreground uppercase tracking-wider">TSB</span>
          <span className={cn(
            'ml-1.5 text-sm font-mono tabular-nums',
            (data?.current_tsb ?? 0) >= 0 ? 'text-[hsl(200,65%,55%)]' : 'text-amber-400',
          )}>
            {data?.current_tsb?.toFixed(1) ?? '—'}
          </span>
        </div>
      </div>

      {/* Chart */}
      <div className="h-52 sm:h-64 lg:h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="clCtlGlow" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(170, 80%, 45%)" stopOpacity={0.15} />
                <stop offset="95%" stopColor="hsl(170, 80%, 45%)" stopOpacity={0} />
              </linearGradient>
              <filter id="clNeonGlow">
                <feGaussianBlur stdDeviation="2" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            <CartesianGrid strokeDasharray="3 6" stroke="rgba(255,255,255,0.04)" vertical={false} />

            <XAxis
              dataKey="dateLabel"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}
              interval="preserveStartEnd"
              minTickGap={40}
            />

            <YAxis
              yAxisId="left"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.25)', fontFamily: 'monospace' }}
              width={35}
            />

            <YAxis
              yAxisId="right"
              orientation="right"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.25)', fontFamily: 'monospace' }}
              width={35}
            />

            <Tooltip content={<PMCTooltip />} />

            <ReferenceLine yAxisId="right" y={0} stroke="rgba(255,255,255,0.08)" strokeDasharray="4 4" />

            <Area
              yAxisId="left"
              type="monotone"
              dataKey="ctl"
              stroke="none"
              fill="url(#clCtlGlow)"
              fillOpacity={1}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="ctl"
              stroke="hsl(170, 80%, 45%)"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 3, fill: 'hsl(170, 80%, 45%)', stroke: 'none' }}
              name="CTL"
              filter="url(#clNeonGlow)"
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="atl"
              stroke="hsl(35, 85%, 55%)"
              strokeWidth={1.5}
              dot={false}
              activeDot={{ r: 3, fill: 'hsl(35, 85%, 55%)', stroke: 'none' }}
              name="ATL"
              strokeDasharray="4 2"
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="tsb"
              stroke="hsl(200, 65%, 55%)"
              strokeWidth={1.5}
              dot={false}
              activeDot={{ r: 3, fill: 'hsl(200, 65%, 55%)', stroke: 'none' }}
              name="TSB"
            />

            <Brush
              dataKey="dateLabel"
              height={24}
              stroke="rgba(255,255,255,0.08)"
              fill="rgba(12,16,24,0.9)"
              tickFormatter={() => ''}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <p className="text-[9px] font-mono text-white/25 tracking-wider text-center mt-2 uppercase">
        [SYS] Engine-computed EWMA · CTL tau=42d · ATL tau=7d
      </p>
    </div>
  );
}
